import {ApiError} from './auth.api-adapter.js';
import {PUBLISH_SNAPSHOT_EVENT, PublishJobStatus, PublishStage} from '../../../core/vocabulary.js';

export class PageApiAdapter {
    #baseUrl;
    #token;

    /** @param {string} baseUrl - e.g. '/api/clients'
     *  @param {string} token - JWT token */
    constructor(baseUrl, token) {
        this.#baseUrl = baseUrl;
        this.#token = token;
    }

    /** @returns {Promise<import('../../types/user.types.js').TemplateSummary[]>} */
    async getTemplates() {
        return this.#request('/templates');
    }

    /**
     * @param {string} templateName
     * @returns {Promise<import('../../types/user.types.js').TemplateDetailResponse>}
     */
    async getTemplate(templateName) {
        return this.#request(`/templates/${encodeURIComponent(templateName)}`);
    }

    /**
     * @param {FormData} formData
     * @returns {Promise<string>} PR URL
     */
    async publishPage(formData) {
        const response = await fetch(`${this.#baseUrl}/pages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {string} [template]
     * @param {number} [page]
     * @param {number} [size]
     * @param {import('../../../core/vocabulary.js').PageSort} [sort] - one of date-desc, date-asc, title-asc, title-desc, template
     * @returns {Promise<import('../../types/user.types.js').PageListResponse>}
     */
    async getPages(template, page, size, sort) {
        const params = new URLSearchParams();
        if (template) params.append('template', template);
        if (page !== undefined) params.append('page', page);
        if (size !== undefined) params.append('size', size);
        if (sort) params.append('sort', sort);

        const queryString = params.toString() ? `?${params.toString()}` : '';
        return this.#request(`/pages${queryString}`);
    }

    /**
     * @param {string} pagePath
     * @returns {Promise<import('../../types/user.types.js').ManifestEntry>}
     */
    async getPageDetail(pagePath) {
        return this.#request(`/pages/${encodeURI(pagePath)}`);
    }

    /**
     * @param {string} templateName
     * @returns {Promise<import('../../types/user.types.js').TemplateLayoutEntry[]>}
     */
    async getTemplateLayout(templateName, context = null) {
        const params = new URLSearchParams({templateName});
        if (context) {
            params.append('context', context);
        }
        return this.#request(`/pages/layout?${params.toString()}`);
    }

    /**
     * @param {string} templateName
     * @param {string|null} pagePath
     * @param {Object<string, *>} data
     * @param {boolean} [prioritizePosition]
     * @returns {Promise<{before:Array,after:Array,movedPaths:string[],hardConflicts:string[]}>}
     */
    async previewLayout(templateName, pagePath, context, data, prioritizePosition = false) {
        const response = await fetch(`${this.#baseUrl}/pages/layout/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.#token}`,
            },
            body: JSON.stringify({templateName, pagePath, context, data: data || {}, prioritizePosition: Boolean(prioritizePosition)}),
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.json();
    }

    /**
     * @param {FormData} formData
     * @returns {Promise<string>} PR URL
     */
    async editPage(formData) {
        const response = await fetch(`${this.#baseUrl}/pages`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {string} pagePath
     * @param {string} templateName
     * @returns {Promise<string>} PR URL
     */
    async deletePage(pagePath, templateName) {
        const params = new URLSearchParams({pagePath, templateName});
        const response = await fetch(`${this.#baseUrl}/pages?${params}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {string} templateName
     * @param {Object} data
     * @returns {Promise<string>} Rendered HTML
     */
    async previewPage(templateName, data) {
        const response = await fetch(`${this.#baseUrl}/templates/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.#token}`,
            },
            body: JSON.stringify({templateName, data}),
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {FormData} formData
     * @returns {Promise<string>} target path
     */
    async stagePage(formData) {
        const response = await fetch(`${this.#baseUrl}/pages/stage`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {FormData} formData
     * @returns {Promise<string>} target path
     */
    async stageEdit(formData) {
        const response = await fetch(`${this.#baseUrl}/pages/stage`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {number} stagedId
     * @returns {Promise<string>}
     */
    async discardDraft(stagedId) {
        const response = await fetch(`${this.#baseUrl}/pages/staged/${stagedId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.text();
    }

    /**
     * @param {(event: {event: string, data: any}) => void} [onEvent]
     * @param {string|null} [path] the target path of the one draft to publish; all drafts when
     *     null or undefined. Any other value is sent as is, so a blank path reaches the server,
     *     which refuses it (400 `ValidationError`) instead of publishing every draft.
     * @returns {Promise<string>}
     */
    async publishStagedAsync(onEvent, path = null) {
        const query = path === null || path === undefined ? '' : `?path=${encodeURIComponent(path)}`;
        const startResponse = await fetch(`${this.#baseUrl}/pages/publish-staged/async${query}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
        });

        if (!startResponse.ok) {
            throw await ApiError.fromResponse(startResponse);
        }

        const startPayload = await startResponse.json();
        const jobId = startPayload?.jobId;
        if (!jobId) {
            throw new ApiError(500, 'Failed to start publish job.');
        }

        return this.#streamPublishStagedJob(jobId, onEvent);
    }

    /**
     * @param {string} jobId
     * @param {(event: {event: string, data: any}) => void} [onEvent]
     * @returns {Promise<string>}
     */
    async #streamPublishStagedJob(jobId, onEvent) {
        const response = await fetch(`${this.#baseUrl}/pages/publish-staged/jobs/${encodeURIComponent(jobId)}/events`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${this.#token}`,
                'Accept': 'text/event-stream',
            },
        });

        if (!response.ok || !response.body) {
            throw await ApiError.fromResponse(response, 'Failed to open publish event stream.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const {done, value} = await reader.read();
            if (done) {
                break;
            }

            buffer += decoder.decode(value, {stream: true});
            const chunks = buffer.split(/\r?\n\r?\n/);
            buffer = chunks.pop() || '';

            for (const chunk of chunks) {
                const parsed = this.#parseSseChunk(chunk);
                if (!parsed) {
                    continue;
                }
                const terminalResult = this.#handlePublishEvent(parsed, onEvent);
                if (terminalResult) {
                    return terminalResult;
                }
            }
        }

        if (buffer.trim().length > 0) {
            const parsed = this.#parseSseChunk(buffer);
            if (parsed) {
                const terminalResult = this.#handlePublishEvent(parsed, onEvent);
                if (terminalResult) {
                    return terminalResult;
                }
            }
        }

        throw new ApiError(500, 'Publish stream ended before completion.');
    }

    /**
     * @param {{event: string, data: any}} parsed
     * @param {(event: {event: string, data: any}) => void} [onEvent]
     * @returns {string | null}
     */
    #handlePublishEvent(parsed, onEvent) {
        onEvent?.(parsed);
        if (parsed.event === PublishStage.COMPLETED) {
            return parsed.data?.result || 'Success';
        }
        if (parsed.event === PublishStage.FAILED) {
            throw this.#publishJobFailure(parsed.data);
        }
        if (parsed.event === PUBLISH_SNAPSHOT_EVENT) {
            const snapshotStatus = typeof parsed.data?.status === 'string'
                ? parsed.data.status.toUpperCase()
                : '';
            if (snapshotStatus === PublishJobStatus.COMPLETED) {
                return parsed.data?.result || 'Success';
            }
            if (snapshotStatus === PublishJobStatus.FAILED) {
                throw this.#publishJobFailure(parsed.data);
            }
        }
        return null;
    }

    /**
     * The error for a failed publish job, carrying the job's error code (for example `NoDrafts`
     * when another session published the drafts first) so it can be told apart from a crash.
     * When the job carries an `errorMessage`, the backend says the client can fix the failure
     * (a draft to correct, an address already in use), so the error gets status 400 and that
     * message; otherwise it is a 500 with a generic message.
     * @param {any} data the job snapshot
     * @returns {ApiError}
     */
    #publishJobFailure(data) {
        const code = typeof data?.errorCode === 'string' && data.errorCode ? data.errorCode : null;
        const message = typeof data?.errorMessage === 'string' ? data.errorMessage.trim() : '';
        return message ? new ApiError(400, message, code) : new ApiError(500, 'Publish job failed.', code);
    }

    /**
     * @param {string} chunk
     * @returns {{event: string, data: any} | null}
     */
    #parseSseChunk(chunk) {
        if (!chunk) {
            return null;
        }
        let eventName = 'message';
        const dataLines = [];
        for (const rawLine of chunk.split('\n')) {
            const line = rawLine.trim();
            if (line.startsWith('event:')) {
                eventName = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.substring(5).trim());
            }
        }

        if (dataLines.length === 0) {
            return {event: eventName, data: null};
        }
        const dataText = dataLines.join('\n');
        try {
            return {event: eventName, data: JSON.parse(dataText)};
        } catch {
            return {event: eventName, data: dataText};
        }
    }

    /**
     * @param {string} path
     * @returns {Promise<Blob>}
     */
    async getAsset(path) {
        const response = await fetch(`${this.#baseUrl}/assets?path=${encodeURIComponent(path)}`, {
            headers: {
                'Authorization': `Bearer ${this.#token}`,
            },
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.blob();
    }

    /**
     * Renders every draft onto the preview of the site; the preview is built afterwards. The answer
     * names the preview commit, to check the build of exactly that commit.
     * @returns {Promise<{status: string, url: string|null, commit: string|null}>}
     */
    async startSitePreview() {
        return this.#request('/site-preview', {method: 'POST'});
    }

    /**
     * Where the preview of the site stands, with its address once it is ready.
     * @param {string|null} [commit] the preview commit a build answered; the preview branch when absent
     * @returns {Promise<{status: string, url: string|null}>}
     */
    async getSitePreview(commit = null) {
        return this.#request(commit ? `/site-preview?commit=${encodeURIComponent(commit)}` : '/site-preview');
    }

    async #request(path, options = {}) {
        const response = await fetch(`${this.#baseUrl}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.#token}`,
                ...options.headers,
            },
        });

        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }

        return response.json();
    }
}
